// Single source of truth for the documentation navigation tree. The
// sidebar, breadcrumb, prev/next pager, and sitemap all derive from this.
// `lastModified` is hand-maintained (YYYY-MM-DD) per the sitemap rules in
// frontend/CLAUDE.md: set it to the date a page's content actually
// changes, never to "today" by default and never via `new Date()`.

import type { DocIconName } from "@/components/docs/icons";

export const DOCS_BASE = "/docs";

export type DocPage = {
  title: string;
  slug: string;
  description?: string;
  lastModified: string;
  icon?: DocIconName;
};

export type DocSection = {
  title: string;
  slug: string;
  icon: DocIconName;
  description: string;
  pages: DocPage[];
};

export const DOC_SECTIONS: DocSection[] = [
  {
    title: "Getting Started",
    slug: "getting-started",
    icon: "getting-started",
    description: "Set up your account and learn your way around.",
    pages: [
      {
        title: "Introduction",
        slug: "introduction",
        description: "What Raileon is and how it helps your business.",
        lastModified: "2026-09-27",
      },
      {
        title: "How onboarding works",
        slug: "how-onboarding-works",
        description: "The three steps from your first call to live agents.",
        lastModified: "2026-09-27",
      },
      {
        title: "Your first week",
        slug: "your-first-week",
        description: "A realistic timeline of what to expect.",
        lastModified: "2026-09-30",
      },
      {
        title: "Tour of your dashboard",
        slug: "dashboard-tour",
        description: "Find your way around mission control.",
        lastModified: "2026-09-30",
      },
      {
        title: "Billing and plans",
        slug: "billing-and-plans",
        description: "Your invoice, changing plans, and capping AI spend.",
        lastModified: "2026-09-27",
      },
    ],
  },
  {
    title: "Your AI Agents",
    slug: "your-ai-agents",
    icon: "agents",
    description: "What your agents do and how they get better over time.",
    pages: [
      {
        title: "What your agents do",
        slug: "what-agents-do",
        description: "The types of agents and the work they handle.",
        lastModified: "2026-09-30",
      },
      {
        title: "How your agents learn",
        slug: "how-agents-learn",
        description: "The three ways your agents improve.",
        lastModified: "2026-09-30",
      },
      {
        title: "Reviewing agent work",
        slug: "reviewing-agent-work",
        description: "Approve and correct what your agents do.",
        lastModified: "2026-09-30",
      },
    ],
  },
  {
    title: "Connecting Your Tools",
    slug: "connecting-your-tools",
    icon: "connect",
    description: "Bring in the apps and services you already use.",
    pages: [
      {
        title: "Overview",
        slug: "overview",
        description: "How connections work and what they unlock.",
        lastModified: "2026-09-30",
      },
      {
        title: "Connect an app",
        slug: "connect-an-app",
        description: "The general steps to connect any service.",
        lastModified: "2026-09-30",
      },
      {
        title: "Gmail",
        slug: "gmail",
        icon: "gmail",
        description: "Let your agents read and send email.",
        lastModified: "2026-09-30",
      },
      {
        title: "Google Calendar",
        slug: "google-calendar",
        icon: "google-calendar",
        description: "Let your agents read and manage your schedule.",
        lastModified: "2026-09-30",
      },
      {
        title: "Slack",
        slug: "slack",
        icon: "slack",
        description: "Post messages and updates to your workspace.",
        lastModified: "2026-09-26",
      },
      {
        title: "Calendly",
        slug: "calendly",
        icon: "calendly",
        description: "Read meetings and share booking links.",
        lastModified: "2026-09-26",
      },
      {
        title: "HubSpot",
        slug: "hubspot",
        icon: "hubspot",
        description: "Keep your CRM contacts up to date.",
        lastModified: "2026-09-26",
      },
      {
        title: "DocuSign",
        slug: "docusign",
        icon: "docusign",
        description: "Send documents out for signature.",
        lastModified: "2026-09-26",
      },
      {
        title: "QuickBooks",
        slug: "quickbooks",
        icon: "quickbooks",
        description: "Draft invoices in your accounting system.",
        lastModified: "2026-09-30",
      },
    ],
  },
  // NOTE: the "Channels" doc section (Discord/Telegram bots) was removed
  // from this nav (#1179, hide decision, 2026-09-26) — the adapters that
  // deliver messages aren't in the default deploy line. The six pages
  // still exist under docs/channels/* (noindex'd) so a direct link to one
  // doesn't 404, but they're no longer in the sidebar, breadcrumb, pager,
  // or sitemap. See docs/CHANNELS_AND_AUTOMATIONS_DEPLOYMENT.md for what
  // must be true to unpark it.
  {
    title: "Getting the Most from Raileon",
    slug: "getting-the-most",
    icon: "optimize",
    description: "Practical ways to get stronger results.",
    pages: [
      {
        title: "Training your agents",
        slug: "training-your-agents",
        description: "Upload documents to sharpen your agents.",
        lastModified: "2026-09-30",
      },
      {
        title: "Using agent chat",
        slug: "assistant-chat",
        description: "Give instructions in plain language.",
        lastModified: "2026-09-30",
      },
      {
        title: "Reports and activity",
        slug: "reports-and-activity",
        description: "See what your agents have done and what it cost.",
        lastModified: "2026-09-30",
      },
      {
        title: "Tips for the best results",
        slug: "tips-for-best-results",
        description: "Habits that make your agents more effective.",
        lastModified: "2026-06-09",
      },
      {
        title: "Security and your data",
        slug: "security-and-your-data",
        description: "How your information is protected.",
        lastModified: "2026-09-30",
      },
    ],
  },
  {
    title: "Client Tasks",
    slug: "client-tasks",
    icon: "checklist",
    description: "Step-by-step guides for the to-dos on your checklist.",
    pages: [
      {
        title: "Accept your invite and sign in",
        slug: "accept-your-invite",
        description: "Join your Raileon workspace for the first time.",
        lastModified: "2026-09-30",
      },
      {
        title: "Upload your key documents",
        slug: "upload-key-documents",
        description: "Give your agents the context they need from day one.",
        lastModified: "2026-09-30",
      },
      {
        title: "Connect your email and calendar",
        slug: "connect-email-and-calendar",
        description: "Let your agents read and manage email and scheduling.",
        lastModified: "2026-09-30",
      },
      {
        title: "Send us your team's names and emails",
        slug: "invite-your-team",
        description: "Send us your teammates' names and emails.",
        lastModified: "2026-09-30",
      },
      {
        title: "Connect your CRM or accounting tool",
        slug: "connect-your-crm-or-accounting-tool",
        description: "Give your agents access to the tool you run on.",
        lastModified: "2026-09-30",
      },
      {
        title: "Approve your first agent actions",
        slug: "approve-your-first-agent-actions",
        description: "Review and approve what your agents propose.",
        lastModified: "2026-09-30",
      },
      {
        title: "Create a GitHub account",
        slug: "create-a-github-account",
        description: "Send us your username so we can build your site.",
        lastModified: "2026-09-30",
      },
      {
        title: "Create a Cloudflare account",
        slug: "create-a-cloudflare-account",
        description: "Invite the Raileon team to manage your DNS and hosting.",
        lastModified: "2026-09-27",
      },
      {
        title: "Give us domain registrar access",
        slug: "domain-registrar-access",
        description: "Or move your domain to Cloudflare instead.",
        lastModified: "2026-09-30",
      },
      {
        title: "Make your DNS changes",
        slug: "make-dns-changes",
        description: "Add the DNS records your Raileon team gave you.",
        lastModified: "2026-09-30",
      },
      {
        title: "Approve your site before launch",
        slug: "approve-the-site",
        description: "Review your new site before it goes live.",
        lastModified: "2026-09-27",
      },
      {
        title: "Grant Search Console and Analytics access",
        slug: "grant-search-console-access",
        description: "Let your Raileon team see your site's search performance.",
        lastModified: "2026-09-30",
      },
      {
        title: "Tell us your Instagram and Facebook accounts",
        slug: "connect-instagram-and-facebook",
        description: "Drafts only until Meta approves posting access.",
        lastModified: "2026-09-30",
      },
    ],
  },
  {
    title: "Help",
    slug: "help",
    icon: "help",
    description: "Reach a person when you need one.",
    pages: [
      {
        title: "Getting help",
        slug: "getting-help",
        description: "Ways to contact the Raileon team.",
        lastModified: "2026-09-30",
      },
    ],
  },
];

export type FlatDoc = {
  title: string;
  description?: string;
  path: string;
  sectionTitle: string;
  sectionSlug: string;
  slug: string;
  lastModified: string;
};

export function flatDocs(): FlatDoc[] {
  const out: FlatDoc[] = [];
  for (const section of DOC_SECTIONS) {
    for (const page of section.pages) {
      out.push({
        title: page.title,
        description: page.description,
        path: `${DOCS_BASE}/${section.slug}/${page.slug}`,
        sectionTitle: section.title,
        sectionSlug: section.slug,
        slug: page.slug,
        lastModified: page.lastModified,
      });
    }
  }
  return out;
}

export function getPrevNext(pathname: string): {
  prev: FlatDoc | null;
  next: FlatDoc | null;
} {
  const flat = flatDocs();
  const i = flat.findIndex((d) => d.path === pathname);
  if (i === -1) return { prev: null, next: null };
  return {
    prev: i > 0 ? flat[i - 1]! : null,
    next: i < flat.length - 1 ? flat[i + 1]! : null,
  };
}

export function getBreadcrumb(
  pathname: string,
): { sectionTitle: string; pageTitle: string } | null {
  const d = flatDocs().find((x) => x.path === pathname);
  if (!d) return null;
  return { sectionTitle: d.sectionTitle, pageTitle: d.title };
}

// Freshest page date drives the /docs index freshness in the sitemap.
export function docsIndexLastModified(): string {
  return flatDocs().reduce(
    (max, d) => (d.lastModified > max ? d.lastModified : max),
    "2026-06-09",
  );
}

export function docsSitemapEntries(): { path: string; lastModified: string }[] {
  return [
    { path: DOCS_BASE, lastModified: docsIndexLastModified() },
    ...flatDocs().map((d) => ({ path: d.path, lastModified: d.lastModified })),
  ];
}
